import { useStateDataGrid } from 'features/shared/components/dataGrid/hooks/useStateDataGrid';
import { loadDataFilterConfig } from 'features/shared/helpers/modules';
import React from 'react';
import DataGridRow from 'features/shared/components/dataGrid/DataGridRow';
import NoContentWrapper from 'features/shared/components/noContentWrapper/NoContentWrapper';
import DataGridHeader from 'features/shared/components/dataGrid/DataGridHeader';
import DataFilterModal from 'features/shared/components/dataFilterModal/DataFilterModal';
import Pagination from 'features/shared/components/pagination/Pagination';
import { useTranslation } from 'react-i18next';
import { Contract_Documents } from 'data/endpoints';
import DataQuery from 'data/DataQuery';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import { useQuery } from 'data/hooks/useQuery';
import PagedDataResultDTO from 'features/shared/dtos/PagedDataResultDTO';
import DocumentSimplifiedDTO from 'features/invoices/dtos/DocumentSimplifiedDTO';
import ContractDocument from 'features/contracts/components/ContractDocument';
import ContractDocumentHeader from 'features/contracts/components/ContractDocumentHeader';

const documentsDataFilterJson = loadDataFilterConfig("contractDocumentsDataFilter");
const pageSize = 10;

type Props = {
  contractId: string;
}

const ContractDocumentsSection = ({ contractId }: Props) => {
  // translations
  const tLabels = useTranslation("contracts\\contracts").t;
  
  // data grid hook
  const {
    dataGrid,
    descriptors,
    setDescriptors,
    showFilter,
    setShowFilter,
    paginationPageChangeHandler,
    filterApplyHandler,
    filterResetHandler,
    isApplyFilter,
    conditionalRefresh,
  } = useStateDataGrid(pageSize, documentsDataFilterJson);

  // fetch data
  const query: DataQuery = {
    url: Contract_Documents,
    params: new URLSearchParams(dataGrid.computedUrlSearchParams),
    routeParams: getRouteParams("contractId", contractId)
  };
  const { data: documents, loadStatus: documentsLoadStatus } = useQuery<PagedDataResultDTO<DocumentSimplifiedDTO>>(
    query,
    { count: 0, result: [] },
    [dataGrid.computedUrlSearchParams, contractId],
    { useTracker: true },
    conditionalRefresh
  );

  //sub-renders
  const renderDocuments= () => {
    return documents.result.map((item) =>
      <DataGridRow key={item.documentId}>
        <ContractDocument document={item} />
      </DataGridRow>
    );  
  } 

  //render
  return (
    <NoContentWrapper
      data={documents}
      loadStatus={documentsLoadStatus}
      noContentAsAlert
      isActiveFilter={isApplyFilter}
      noContentInfo={tLabels("contractDocumentsSection.noDocumentsFound")}
    >
      <DataGridHeader 
        count={documents.count}
        setShowFilterModal={setShowFilter}
        filterActive={isApplyFilter()}
      />
      <ContractDocumentHeader />
      {renderDocuments()}
      <div className="d-flex justify-content-center mt-4">
        <Pagination
          urlParams={new URLSearchParams(dataGrid.computedUrlSearchParams)}
          count={documents.count!}
          pageSize={pageSize}
          onPageChange={paginationPageChangeHandler}
        />
      </div>

      <DataFilterModal
        descriptors={descriptors}
        setDescriptors={setDescriptors}
        applyButtonOnClick={filterApplyHandler}
        resetButtonOnClick={filterResetHandler}
        show={showFilter}
        setShow={setShowFilter}
      />
    </NoContentWrapper>
  );
}

export default ContractDocumentsSection